import { ExclamationCircleIcon } from "@heroicons/react/24/outline"
import { useMutation, useQuery } from "@tanstack/react-query"
import { Checkbox, Form, Input, Radio, Select, Space, Tooltip } from "antd"
import { Button } from "components"
import { Dispatch, SetStateAction, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import type { IMultipleFormData } from "../contractor/ContractorPaymentSchedule"
import BeneficiaryAmountSettings, {
  hasMissingRate,
  sampleAmountSetting,
  useConversionRates,
  type AmountSetting,
} from "../components/BeneficiaryAmountSettings"
import { AntDesignRadio } from "components/antd/AntDesignRadio"
import { ICurrencies } from "pages/dashboard/wallets/integration/interface/kyc"
import { ISplitEmployeeData } from "../interface"
import { getSplitPaymentConfig } from "./SingleEmployee"
import { IWalletBalance } from "pages/dashboard/wallets/interface/wallet"
import {
  paymentSchedulesService,
  PAYMENT_SCHEDULES_ENDPOINTS,
} from "services/paymentSchedulesService"
import { onQueryError } from "utils/utils"
import {
  adminPeopleService,
  ADMIN_PEOPLE_ENDPOINTS,
} from "services/adminPeopleService"
import { IEmployeeRes } from "../../Transfer"
import { IContractorsRes } from "../../typings"

const MultipleEmployee = ({
  setFormBodyMultipleEmp,
  formBodyMultipleEmp,
  setFormBodyMultipleCont,
  formBodyMultipleCont,
  currencies,
  setShowPreview,
  showPreview,
  setLoading,
  balances,
  getCurrencySymbol,
}: {
  setFormBodyMultipleEmp: React.Dispatch<SetStateAction<IMultipleFormData>>
  formBodyMultipleEmp: IMultipleFormData
  setFormBodyMultipleCont: Dispatch<SetStateAction<IMultipleFormData>>
  formBodyMultipleCont: IMultipleFormData
  currencies: IBaseRes<ICurrencies[]> | undefined
  setShowPreview: (e: boolean) => void
  showPreview: boolean
  setLoading: (e: boolean) => void
  balances?: IWalletBalance[]
  getCurrencySymbol: (code: string | undefined) => string | undefined
}) => {
  const [form] = Form.useForm()
  const includeExitedValue = Form.useWatch("includeExited", form)
  const description = Form.useWatch("description", form)
  const [descriptionName, setDescriptionName] = useState("")
  const [isNameModified, setIsNameModified] = useState(false)
  const [addContToPayment, setAddContToPayment] = useState(false)
  const [empAmount, setEmpAmount] = useState<AmountSetting>(sampleAmountSetting)
  const [contAmount, setContAmount] =
    useState<AmountSetting>(sampleAmountSetting)

  const setEmpPersons = (updater: any) =>
    setFormBodyMultipleEmp((prev: any) => ({
      ...prev,
      persons: typeof updater === "function" ? updater(prev.persons) : updater,
    }))
  const setContPersons = (updater: any) =>
    setFormBodyMultipleCont((prev: any) => ({
      ...prev,
      persons: typeof updater === "function" ? updater(prev.persons) : updater,
    }))

  const empRates = useConversionRates(setEmpPersons)
  const contRates = useConversionRates(setContPersons)
  const {
    isFetching: isCheckingScheduleName,
    isError: isCheckNameError,
    error: checkNameError,
  } = useQuery({
    queryFn: () => paymentSchedulesService.checkName(descriptionName, "-1"),
    queryKey: [PAYMENT_SCHEDULES_ENDPOINTS.CHECK_NAME, descriptionName],
    enabled: !!descriptionName,
  })

  const nameError =
    !isNameModified && isCheckNameError
      ? (checkNameError as any)?.response?.data?.message ||
        "Name already in use"
      : ""

  const { mutate: getSplitInfoEmp } = useMutation({
    mutationFn: async (body: any) => {
      const data = await paymentSchedulesService.getPayScheduleBeneficiaries(
        body
      )

      return data
    },
    onSuccess: (data: IBaseRes<ISplitEmployeeData>) => {
      setLoading(false)

      setFormBodyMultipleEmp((prevForm: any) => {
        const updatedPersons = prevForm.persons.map((person: any) => {
          const matchedPerson = data?.data?.beneficiaries?.find(
            (item) => item?.person_id === Number(person.person_id)
          )

          if (matchedPerson) {
            const splitConfig = getSplitPaymentConfig(
              matchedPerson,
              data?.data ?? {},
              person.amount || 0
            )
            if (splitConfig) {
              return { ...person, split: splitConfig }
            }
          }

          return person
        })

        return { ...prevForm, persons: updatedPersons }
      })
    },

    onError: onQueryError,
  })

  const { isLoading: isEmployeeLoading, data: employees } =
    useQuery<IEmployeeRes>({
      queryFn: () =>
        adminPeopleService.getForSelection({ active: !includeExitedValue }),
      queryKey: [
        ADMIN_PEOPLE_ENDPOINTS.GET_FOR_SELECTION,
        { active: !includeExitedValue },
      ],
    })

  const { isLoading: isContractorsLoading, data: contractors } =
    useQuery<IContractorsRes>({
      queryFn: () => adminPeopleService.getContractors(),
      queryKey: [ADMIN_PEOPLE_ENDPOINTS.GET_CONTRACTORS],
    })

  const emptyContAmountField = useMemo(
    () =>
      formBodyMultipleCont?.persons?.find(
        (per: any, idx: any) => per.amount === "" || per.amount === null
      ),
    [formBodyMultipleCont?.persons]
  )

  const emptyEmpAmountField = useMemo(
    () =>
      formBodyMultipleEmp?.persons?.find(
        (per: any, idx: any) => per.amount === "" || per.amount === null
      ),
    [formBodyMultipleEmp?.persons]
  )

  return (
    <Form
      layout="vertical"
      form={form}
    >
      <div className=" mt-6 flex flex-col w-full  gap-y-4">
        <div
          className=""
          id="custom-payment-desc"
        >
          {" "}
          <Form.Item
            className="!mb-0"
            name="description"
            label={
              <span className="font-circular text-padeBlack text-sm  flex items-center">
                Payment Description
                <Tooltip
                  title="Mamixum of 50 characters"
                  trigger={["hover"]}
                >
                  <span>
                    <ExclamationCircleIcon className="w-4 h-4 opacity-60 hover:opacity-100" />
                  </span>
                </Tooltip>
              </span>
            }
            extra={
              nameError !== "" && (
                <span className="text-danger500">{nameError} </span>
              )
            }
            rules={[
              {
                validator: async (_, value) => {
                  const wordCount = value ? value.trim().split(/\s+/).length : 0
                  if (wordCount > 50) {
                    throw new Error("Description must be 50 words or less")
                  }
                },
              },
              { message: "Please enter this field", required: true },
            ]}
          >
            <Input
              maxLength={50}
              value={formBodyMultipleEmp.description}
              placeholder="Enter description"
              onChange={(e) => {
                setIsNameModified(true)
                setFormBodyMultipleEmp({
                  ...formBodyMultipleEmp,
                  description: e.target.value,
                })
              }}
              onBlur={(e) => {
                setIsNameModified(false)
                e.target.value && setDescriptionName(e.target.value)
              }}
            />
          </Form.Item>
        </div>
        <div
          className="relative"
          id="custom-payment-amount"
        >
          <div className="">
            <Form.Item
              label={
                <span className="font-circular text-padeBlack text-sm">
                  Select Employees
                </span>
              }
              name="employees"
              rules={[{ required: true, message: "Please enter this field!" }]}
            >
              <Select
                showSearch
                mode="multiple"
                style={{ width: "100%" }}
                placeholder="Please select"
                loading={isEmployeeLoading}
                maxTagCount={5}
                onSelect={(value: any) => {
                  const person = employees?.data?.find(
                    (emp) => emp.person_id === value
                  )

                  if (!person) return

                  const sharedAmount =
                    empAmount.mode !== "individual" &&
                    empAmount.sharedAmount != null
                      ? String(empAmount.sharedAmount)
                      : ""

                  const sourceCurrency =
                    empAmount.mode === "sameValue"
                      ? empAmount.sharedCurrency
                      : person.currency_code

                  const rate = empRates.rates[person.currency_code as string]
                  const displayAmount =
                    empAmount.mode === "sameValue" && sharedAmount !== ""
                      ? person.currency_code === empAmount.sharedCurrency
                        ? Number(sharedAmount) // same currency → no conversion
                        : rate
                        ? Number((Number(sharedAmount) * rate).toFixed(2))
                        : null
                      : null

                  setFormBodyMultipleEmp({
                    ...formBodyMultipleEmp,
                    persons: [
                      ...formBodyMultipleEmp.persons,
                      {
                        person_name: person.full_name,
                        person_id: String(person.person_id),
                        person_bank:
                          person.bank ??
                          (person.routing_code
                            ? `${person.routing_code}(${person.routing_type})`
                            : "-"),
                        person_account_number: person.account_number,
                        amount: sharedAmount,
                        display_amount: displayAmount,
                        currency_symbol: person.currency_symbol,
                        currency_code: person.currency_code,
                        source_currency_code: sourceCurrency,
                      },
                    ],
                  })

                  if (
                    empAmount.mode === "sameValue" &&
                    person.currency_code &&
                    person.currency_code !== empAmount.sharedCurrency &&
                    !empRates.rates[person.currency_code]
                  ) {
                    empRates.fetchRates(empAmount.sharedCurrency, [
                      person.currency_code,
                    ])
                  }
                }}
                onDeselect={(value: any) => {
                  const filteredPersons = formBodyMultipleEmp.persons?.filter(
                    (emp: any) => emp.person_id !== String(value)
                  )

                  setFormBodyMultipleEmp({
                    ...formBodyMultipleEmp,
                    persons: filteredPersons,
                  })
                }}
                filterOption={(input, option) => {
                  const personName = employees?.data?.find(
                    (emp) => emp.person_id === option?.value
                  )?.full_name

                  return personName
                    ? personName.toLowerCase().includes(input.toLowerCase())
                    : false
                }}
              >
                {employees?.data?.map((person, idx) => (
                  <Select.Option
                    value={person?.person_id}
                    key={idx}
                  >
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <span>{person.full_name}</span>
                      <div>
                        <img
                          src={
                            currencies?.data?.find(
                              (item) => item.code === person.currency_code
                            )?.flagUrl || ""
                          }
                          alt={`${person.currency_code} flag`}
                          style={{
                            width: "18px",
                            height: "18px",
                            marginRight: "8px",
                            marginLeft: "8px",
                          }}
                        />
                        <span>{person.currency_code}</span>
                      </div>
                    </span>
                  </Select.Option>
                ))}

                {["first", "second"].map((item) => (
                  <Select.Option
                    key={"empty" + item}
                    className="pointer-events-none"
                    disabled
                  >
                    {" "}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
            <div className="mt-3">
              <Form.Item
                name="includeExited"
                valuePropName="checked"
                className="!-mt-4"
              >
                <Checkbox>Include exited employees</Checkbox>
              </Form.Item>
            </div>
          </div>
          {/* Add All Button */}
          <button
            className="text-sm text-primary400 font-circular absolute right-0 top-1"
            onClick={(e) => {
              e.preventDefault()
              const allEmployees = employees?.data?.map(
                (item) => item.person_id
              )

              form.setFieldValue("employees", allEmployees)

              const shared = empAmount.mode !== "individual"
              const sharedAmount = shared ? empAmount.sharedAmount ?? "" : ""

              const existingIds = new Set(
                formBodyMultipleEmp.persons.map((p: any) => String(p.person_id))
              )

              const body: any = employees?.data
                ?.filter((person) => !existingIds.has(String(person.person_id)))
                .map((person) => {
                  const rate = empRates.rates[person.currency_code as string]
                  const displayAmount =
                    empAmount.mode === "sameValue" &&
                    sharedAmount !== "" &&
                    sharedAmount != null
                      ? person.currency_code === empAmount.sharedCurrency
                        ? sharedAmount // same currency → no conversion
                        : rate
                        ? Number((Number(sharedAmount) * rate).toFixed(2))
                        : null
                      : null

                  return {
                    person_name: person?.full_name,
                    person_id: String(person?.person_id),
                    person_bank: person.bank
                      ? person.bank
                      : person?.routing_code
                      ? `${person?.routing_code}(${person?.routing_type})`
                      : "-",
                    person_account_number: person.account_number,
                    amount: sharedAmount,
                    display_amount: displayAmount,
                    currency_symbol: person?.currency_symbol,
                    currency_code: person?.currency_code,
                    source_currency_code:
                      empAmount.mode === "sameValue"
                        ? empAmount.sharedCurrency
                        : person?.currency_code,
                  }
                })

              setFormBodyMultipleEmp((prev: any) => ({
                ...prev,
                persons: [...prev.persons, ...body],
              }))

              if (empAmount.mode === "sameValue") {
                empRates.fetchRates(
                  empAmount.sharedCurrency,
                  (body ?? []).map((b: any) => b.currency_code)
                )
              }
            }}
            type="button"
          >
            Add all
          </button>
          {/* ======================= */}
          {/* ===Remove All Button=== */}
          {formBodyMultipleEmp?.persons &&
            formBodyMultipleEmp.persons.length > 0 && (
              <button
                className="text-sm text-primary400 font-circular absolute right-16 top-1"
                onClick={(e) => {
                  e.preventDefault()
                  form.setFieldValue("employees", [])
                  setFormBodyMultipleEmp((prev: any) => ({
                    ...prev,
                    persons: [],
                  }))
                }}
                type="button"
              >
                Remove all
              </button>
            )}
        </div>
        <BeneficiaryAmountSettings
          type="employee"
          idKey="person_id"
          persons={formBodyMultipleEmp.persons}
          setPersons={setEmpPersons}
          value={empAmount}
          onChange={setEmpAmount}
          conversionRates={empRates.rates}
          fetchRates={empRates.fetchRates}
          resetRates={empRates.resetRates}
          balances={balances}
          getCurrencySymbol={getCurrencySymbol}
        />

        {/* ADD Contractor */}
        {/* ADD Contractor */}
        {/* ADD Contractor */}
        {/* ADD Contractor */}
        <AntDesignRadio
          label="Would you like to include contractors in this payment schedule"
          name="access"
          value={addContToPayment}
        >
          <Space direction="horizontal">
            <Radio
              value={true}
              checked={addContToPayment ? true : false}
              onChange={() => {
                setAddContToPayment(true)
              }}
            >
              Yes
            </Radio>
            <Radio
              value={false}
              checked={addContToPayment === false ? true : false}
              onChange={() => {
                setAddContToPayment(false)
              }}
            >
              No
            </Radio>
          </Space>
        </AntDesignRadio>

        {/* CONTRACTORS */}
        {addContToPayment && (
          <div className="">
            <div
              className="mb-4"
              id="custom-payment-amount"
            >
              <Form.Item
                label={
                  <span className="font-circular text-padeBlack text-sm  ">
                    Select Contractors
                  </span>
                }
                name="contractors"
                rules={[
                  { required: true, message: "Please enter this field!" },
                ]}
              >
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="Please select"
                  loading={isContractorsLoading}
                  maxTagCount={5}
                  onSelect={(value: any) => {
                    const person = contractors?.data?.find(
                      (cont) => cont.contractor_id === value
                    )

                    if (!person) return

                    const sharedAmount =
                      contAmount.mode !== "individual" &&
                      contAmount.sharedAmount != null
                        ? String(contAmount.sharedAmount)
                        : ""

                    const sourceCurrency =
                      contAmount.mode === "sameValue"
                        ? contAmount.sharedCurrency
                        : person.currency_code

                    const rate = contRates.rates[person.currency_code as string]
                    const displayAmount =
                      contAmount.mode === "sameValue" && sharedAmount !== ""
                        ? person.currency_code === contAmount.sharedCurrency
                          ? Number(sharedAmount)
                          : rate
                          ? Number((Number(sharedAmount) * rate).toFixed(2))
                          : null
                        : null

                    setFormBodyMultipleCont({
                      ...formBodyMultipleCont,
                      persons: [
                        ...formBodyMultipleCont.persons,
                        {
                          person_name: person?.name,
                          contractor_id: String(person?.contractor_id),
                          person_bank: person.bank
                            ? person.bank
                            : person?.routing_code
                            ? `${person?.routing_code}(${person?.routing_type})`
                            : "-",
                          person_account_number: person.account_number,
                          amount: sharedAmount,
                          display_amount: displayAmount,
                          currency_symbol: person?.currency_symbol,
                          currency_code: person?.currency_code,
                          source_currency_code: sourceCurrency,
                        },
                      ],
                    })

                    if (
                      contAmount.mode === "sameValue" &&
                      person.currency_code &&
                      person.currency_code !== contAmount.sharedCurrency &&
                      !contRates.rates[person.currency_code]
                    ) {
                      contRates.fetchRates(contAmount.sharedCurrency, [
                        person.currency_code,
                      ])
                    }
                  }}
                  onDeselect={(value: any) => {
                    const filteredPersons =
                      formBodyMultipleCont.persons?.filter(
                        (emp: any) => emp.contractor_id !== String(value)
                      )

                    setFormBodyMultipleCont({
                      ...formBodyMultipleCont,
                      persons: filteredPersons,
                    })
                  }}
                  onChange={(values: string[]) => {
                    return []
                  }}
                  filterOption={(input, option) => {
                    const personName = contractors?.data?.find(
                      (cont) => cont.contractor_id === option?.value
                    )?.name

                    return personName
                      ? personName.toLowerCase().includes(input.toLowerCase())
                      : false
                  }}
                >
                  {contractors?.data?.map((person, idx) => (
                    <Select.Option
                      value={person?.contractor_id}
                      key={idx}
                    >
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "space-between",
                        }}
                      >
                        <span>{person.name}</span>
                        <div>
                          <img
                            src={
                              currencies?.data?.find(
                                (item) => item.code === person.currency_code
                              )?.flagUrl || ""
                            }
                            alt={`${person?.currency_code} flag`}
                            style={{
                              width: "18px",
                              height: "18px",
                              marginRight: "8px",
                              marginLeft: "8px",
                            }}
                          />
                          <span>{person.currency_code}</span>
                        </div>
                      </span>
                    </Select.Option>
                  ))}

                  {["first", "second"].map((item) => (
                    <Select.Option
                      key={"empty" + item}
                      className="pointer-events-none"
                      disabled
                    >
                      {" "}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </div>
            <BeneficiaryAmountSettings
              type="contractor"
              idKey="contractor_id"
              persons={formBodyMultipleCont.persons}
              setPersons={setContPersons}
              value={contAmount}
              onChange={setContAmount}
              conversionRates={contRates.rates}
              fetchRates={contRates.fetchRates}
              resetRates={contRates.resetRates}
              balances={balances}
              getCurrencySymbol={getCurrencySymbol}
            />
          </div>
        )}

        <div className="flex  items-center justify-end w-full gap-6  pt-4 bg-white border-t mt-14 shadow-footer ">
          <Link to="/dashboard/payment-schedules/custom">
            <Button
              title="Previous"
              color="secondary"
            >
              Cancel
            </Button>
          </Link>
          <Button
            color="primary"
            onClick={(e) => {
              e.preventDefault()
              setShowPreview(true)
              setLoading(true)

              getSplitInfoEmp(
                formBodyMultipleEmp?.persons?.map(
                  (person: any) => person?.person_id
                )
              )
            }}
            disabled={
              emptyContAmountField ||
              emptyEmpAmountField ||
              (empAmount.mode === "sameValue" &&
                hasMissingRate(
                  formBodyMultipleEmp.persons,
                  empAmount.sharedCurrency,
                  empRates.rates
                )) ||
              (contAmount.mode === "sameValue" &&
                hasMissingRate(
                  formBodyMultipleCont.persons,
                  contAmount.sharedCurrency,
                  contRates.rates
                )) ||
              (!description && formBodyMultipleCont.persons.length == 0)
                ? true
                : false
            }
          >
            Preview
          </Button>
        </div>
      </div>
    </Form>
  )
}

export default MultipleEmployee
